import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, AuthCard, AuthField, Button, ErrorBanner } from '@scoring/frontend-shared';

/**
 * Register page — admin self sign-up via Cognito. Two steps:
 *   1. Enter email + password → Cognito emails a verification code.
 *   2. Enter the code → account is confirmed and signed in.
 */
export function RegisterPage(): JSX.Element {
  const { signUp, confirmSignUp, signIn, status } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'details' | 'confirm'>('details');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (status === 'authenticated') {
    void navigate('/', { replace: true });
  }

  const handleRegister = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await signUp({ email, password });
      setStep('confirm');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await confirmSignUp(email, code);
      await signIn({ email, password });
      void navigate('/', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <AuthCard title="Confirm Your Email" subtitle={`Enter the code we sent to ${email}`}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <form onSubmit={(e) => void handleConfirm(e)}>
          <AuthField id="code" label="Verification code" value={code} onChange={setCode} inputMode="numeric" autoComplete="one-time-code" required />
          <Button type="submit" block disabled={loading}>
            {loading ? 'Confirming…' : 'Confirm & Sign In'}
          </Button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Create Admin Account"
      subtitle="Set up events, teams, and live scoring"
      footer={<>Already have an account? <Link to="/login">Sign in</Link></>}
    >
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <form onSubmit={(e) => void handleRegister(e)}>
        <AuthField id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
        <AuthField id="password" label="Password" type="password" value={password} onChange={setPassword} autoComplete="new-password" required />
        <AuthField id="confirmPassword" label="Confirm password" type="password" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" required />
        <Button type="submit" block disabled={loading}>
          {loading ? 'Creating account…' : 'Create Account'}
        </Button>
      </form>
    </AuthCard>
  );
}
